import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import EmbeddableEditor from './pages/HtmlEditorTestPage/components/EmbeddableEditor/EmbeddableEditor';
import TestPage from './pages/HtmlEditorTestPage/TestPage';
import HomePage from './pages/HomePage/HomePage';
import DataSourcesPage from './pages/DataSourcesPage/DataSourcesPage';
import ApplicationsPage from './pages/ApplicationsPage/ApplicationsPage';
import CallbackPage from './pages/CallbackPage/CallbackPage';
import CognitoCallbackPage from './pages/CognitoCallbackPage/CognitoCallbackPage';
import { getExternalAuthConfig } from './shared/auth/cognitoAuth';
import { COGNITO_CALLBACK_PATH } from './shared/config/externalAuthConfig';
import { CognitoProvider } from './shared/contexts/CognitoContext';
import { FederateProvider, useFederate } from './shared/contexts/FederateContext';
import { RegionProvider, useRegion } from './shared/contexts/RegionContext';
import { isAdminUser, nonAdminHome } from './utils/roleUtils';
import './global.scss';

// The merged Knowledge Engine SPA is a large, independent bundle; lazy-load it so it is
// only fetched when a user actually enters /knowledge-engine/*. Imported via the
// `knowledge-engine-app` boundary specifier (type-stub in knowledge-engine-app.d.ts,
// aliased to the real entry in vite.config.ts) so the host's strict tsc does not recurse
// into the KE subtree, which is type-checked on its own (relaxed) config.
const KnowledgeEngineApp = lazy(() => import('knowledge-engine-app'));

// Media Processing pages moved from IVT, lazy-loaded through the `media-processing-app`
// boundary specifier in the same way. Their API is in us-east-1, so global region only.
const MediaProcessingApp = lazy(() => import('media-processing-app'));

// KE is GLOBAL-only (no China backend). When the Portal is in the China region the
// feature is disabled, so send the user back to the dashboard rather than mounting a
// SPA that cannot reach any backend.
function KnowledgeEngineGate() {
  const { currentRegion } = useRegion();
  if (currentRegion !== 'global') {
    return <Navigate to="/" replace />;
  }
  return (
    <Suspense fallback={<div style={{ padding: 24 }}>Loading Knowledge Engine…</div>}>
      <KnowledgeEngineApp />
    </Suspense>
  );
}

// Media Processing signs in through the Cognito Hosted UI (email/password for external
// users, "Sign in with Federate" for internal users) wherever a Hosted UI is configured
// for the stage; elsewhere it stays on the Portal's Federate login.
function MediaProcessingGate() {
  const { currentRegion } = useRegion();
  if (currentRegion !== 'global') {
    return <Navigate to="/overview" replace />;
  }
  const app = (
    <Suspense fallback={<div style={{ padding: 24 }}>Loading Media Processing…</div>}>
      <MediaProcessingApp />
    </Suspense>
  );
  const externalAuthConfig = getExternalAuthConfig();
  if (externalAuthConfig) {
    return <CognitoProvider config={externalAuthConfig}>{app}</CognitoProvider>;
  }
  return <FederateProvider>{app}</FederateProvider>;
}

// The Content Connection pages are admin-only: the service refuses every operation for a
// non-admin, so send them to a page they can use instead of rendering failing API calls.
function AdminGate() {
  const { userRoles } = useFederate();
  const { currentRegion } = useRegion();
  if (!isAdminUser(userRoles)) {
    return <Navigate to={nonAdminHome(currentRegion)} replace />;
  }
  return <Outlet />;
}

function FederateRoutesLayout() {
  return (
    <FederateProvider>
      <Outlet />
    </FederateProvider>
  );
}

function App() {
  return (
    <RegionProvider>
      <Router>
        <Routes>
          {/* OIDC callback — must NOT be wrapped by FederateProvider, since the cookie is being set here */}
          <Route path="/callback" element={<CallbackPage />} />
          {/* Cognito Hosted UI callback (code + PKCE) for the Media Processing pages. */}
          <Route path={COGNITO_CALLBACK_PATH} element={<CognitoCallbackPage />} />
          {/* Routes that require federate authentication */}
          <Route element={<FederateRoutesLayout />}>
            <Route element={<AdminGate />}>
              <Route path="/overview" element={<HomePage />} />
              <Route path="/collections" element={<DataSourcesPage />} />
              <Route path="/applications" element={<ApplicationsPage />} />
            </Route>
            <Route path="/html-editor" element={<EmbeddableEditor />} />
            <Route path="/html-editor/test" element={<TestPage />} />
            <Route path="/artifacts/:artifactId" element={<EmbeddableEditor />} />
            {/* Merged Knowledge Engine SPA (global region only); owns its own nested routes. */}
            <Route path="/knowledge-engine/*" element={<KnowledgeEngineGate />} />
          </Route>
          {/* Media Processing is the landing page; it owns /, /translation, /dubbing, /rebranding, /studio, /glossary and /voice-library.
              It provides its own auth (see MediaProcessingGate), so it sits outside FederateRoutesLayout. */}
          <Route path="/*" element={<MediaProcessingGate />} />
        </Routes>
      </Router>
    </RegionProvider>
  );
}

export default App;
